<template>
  <label
    class="checkbox"
    :class="{'has-error': hasError}"
  >
    <span class="checkbox__content">
      <input
        class="checkbox__input"
        v-model="internalValue"
        :name="name"
        :indeterminate.prop="indeterminate"
        :disabled="disabled"
        :value="value"
        type="checkbox"
      >
      <span class="checkbox__marker">
        <icon icon-name="check-bold"></icon>
      </span>
      <span
        class="checkbox__text"
        v-if="label && !$scopedSlots.label" v-html="label"
      ></span>
    </span>
    <span
      class="checkbox__text"
      v-if="$scopedSlots.label"
    >
      <slot name="label"></slot>
    </span>
  </label>
</template>

<script>
export default {
  name: 'Checkbox',
  model: {
    prop: 'inputModel',
  },
  props: {
    inputModel: {
      default: '',
    },
    label: String,
    name: String,
    value: {
      default: '',
    },
    indeterminate: Boolean,
    disabled: Boolean,
    hasError: Boolean,
  },
  computed: {
    internalValue: {
      get() {
        return this.inputModel;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/utils/vars';
  @import '@/assets/scss/utils/mixins';

  $checkbox-border-color: map_get($color-neutral-black, 45);
  $checkbox-border-radius: 2px;

  .checkbox {
    display: inline-block;
    vertical-align: middle;
    @include text-md;
    font-weight: normal;
    user-select: none;
    padding-top: 8px;
    padding-bottom: 8px;
    max-width: 100%;

    &__content {
      display: flex;
      align-items: flex-start;
      position: relative;
      min-height: 24/16*1em;
    }

    &__input {
      opacity: 0;
      position: absolute;
      top: 0.15em;
      left: 0;
      width: 18px;
      height: 18px;
      margin: 0;
      z-index: 1;
      cursor: pointer;

      &[disabled] {

        &, & ~ * {
          cursor: not-allowed;
        }
      }
    }

    &__marker {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      border: 1px solid $checkbox-border-color;
      border-radius: $checkbox-border-radius;
      position: relative;
      top: 0.15em;
      cursor: pointer;
      color: $color-white;
      fill: $color-white;
      transition: $transition-default;

      &::before {
        content: "";
        position: absolute;
        top: -6px;
        left: -6px;
        right: -6px;
        bottom: -6px;
        border: 3px solid rgba($color-primary, 0.65);
        border-radius: 6px;
        opacity: 0;
        transition: $transition-default opacity;
      }

      &::after {
        content: "";
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 8px;
        height: 8px;
        background-color: $color-primary;
        opacity: 0;
        transition: $transition-default opacity;
      }

      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 12px;
        height: 12px;
        transform: translate(-50%, -50%);
        opacity: 0;
      }

      .checkbox:hover & {
        border-color: $color-primary;
      }

      .checkbox.has-error & {
        border-color: $color-danger;
      }

      .checkbox__input:focus ~ & {

        &::before {
          opacity: 1;
        }
      }

      .checkbox__input:checked ~ & {
        background-color: $color-primary;
        border-color: transparent;

        svg {
          opacity: 1;
        }
      }

      .checkbox__input:indeterminate ~ & {

        &::after {
          opacity: 1;
        }
      }

      .checkbox__input[disabled] ~ & {
        cursor: default;
        background-color: map-get($color-neutral-black, 4);
        color: map-get($color-neutral-black, 25);
        fill: map-get($color-neutral-black, 25);
        border-color: map-get($color-neutral-black, 15);

        &::after {
          background-color: map-get($color-neutral-black, 25);
        }

        &::before {
          opacity: 0;
        }
      }
    }

    &__text {
      padding-left: 12/16*1em;

      .checkbox__input[disabled] ~ & {
        color: map-get($color-neutral-black, 25);
      }
    }
  }
</style>
